<template>
  <LcSlider
    v-bind="{ max, min, marker, ...$attrs }"
    v-model:value="internalValue"
    class="w-full"
    container-class="w-full"
    :format="toMoney"
    :lazy="false"
    no-feedback
  />
</template>

<script lang="ts" setup>
import Money from '~~/models/Money'

type Charge = { id: string; value: number }

type Emits = {
  (event: 'update:value', value: Charge['id']): void
}

type Props = { charges: Charge[]; value: Charge['id'] }

const byValueAscending = (a: Charge, b: Charge) => a.value - b.value

const emit = defineEmits<Emits>()
const { charges, value } = defineProps<Props>()

const internalValue = computed({
  get: () => getChargeById(value)?.value ?? 0,
  set: (newValue) => {
    const newCharge = getChargeByValue(newValue)
    if (newCharge) emit('update:value', newCharge.id)
  },
})

const max = computed(() => sortedCharges.value.at(-1)?.value ?? 1)

const min = computed(() => sortedCharges.value.at(0)?.value ?? 0)

const marker = computed(() => {
  const chargesInBetween = sortedCharges.value.filter(without([max.value, min.value]))

  return {
    snap: true,
    steps: Object.fromEntries(chargesInBetween.map(toMarker)),
  }
})

const sortedCharges = computed(() => [...charges].sort(byValueAscending))

const getChargeByValue = (searchedValue: Charge['value']): Maybe<Charge> =>
  charges.find(({ value }) => value === searchedValue) ?? null

const getChargeById = (searchedId: Charge['id']): Maybe<Charge> =>
  charges.find(({ id }) => id === searchedId) ?? null

const toMarker = ({ value }: Charge, index: number) => {
  const key = (100 / (charges.length - 1)) * (index + 1)
  return [key, value]
}

const toMoney = (value: number): string => new Money(value / 100).toString({ fractionDigits: 0 })

const without =
  (numbers: number[]) =>
  ({ value }: Charge) =>
    !numbers.includes(value)

;(async () => {
  await until(sortedCharges).toMatch((c) => c.length > 0)

  if (!value && sortedCharges.value.length > 0) {
    emit('update:value', sortedCharges.value[0].id)
  }
})()
</script>
